/**
 * The child's star goal (kid-first redesign D4, Jens 2026-10-11): automatic, no picking needed.
 *
 * Only the child's own unclaimed individual rewards count (family rewards are the family goal).
 * Progress uses each reward's eligible_points — stars earned since that reward's own start date
 * (stars are never spent). Rule:
 *   1. A reward the child can get now ("ready") wins — the cheapest of those.
 *   2. Otherwise the reward that needs the fewest more stars.
 * Returns null when the child has no rewards to aim for.
 */
import type { Reward } from '../shared/types';

export interface StarGoal {
  reward: Reward;
  have: number;     // stars counting toward it
  need: number;     // points_required
  remaining: number; // 0 when ready
  ready: boolean;
}

export function isMyIndividualReward(r: Reward, userId: string): boolean {
  return r.target_type === 'individual' && !r.is_claimed && (!r.target_id || r.target_id === userId);
}

export function pickStarGoal(rewards: Reward[] | undefined | null, userId: string): StarGoal | null {
  const mine = (rewards || []).filter((r) => isMyIndividualReward(r, userId) && (r.points_required ?? 0) > 0);
  if (!mine.length) return null;
  const goals = mine.map((reward) => {
    const have = Math.max(0, Number(reward.eligible_points ?? 0));
    const need = Number(reward.points_required);
    const remaining = Math.max(0, need - have);
    return { reward, have, need, remaining, ready: remaining === 0 };
  });
  const ready = goals.filter((g) => g.ready).sort((a, b) => a.need - b.need);
  if (ready.length) return ready[0];
  return goals.sort((a, b) => a.remaining - b.remaining || a.need - b.need)[0];
}

/**
 * The family goal on Home (D5, replaces the sibling leaderboard): the family's unclaimed family
 * rewards, same rule as the star goal — a ready one first (cheapest), otherwise the one needing
 * the fewest more stars. Family rewards count everyone's stars since their own start date.
 */
export function pickFamilyGoal(rewards: Reward[] | undefined | null): StarGoal | null {
  const fam = (rewards || []).filter((r) => r.target_type === 'family' && !r.is_claimed && (r.points_required ?? 0) > 0);
  if (!fam.length) return null;
  const goals = fam.map((reward) => {
    const have = Math.max(0, Number(reward.eligible_points ?? 0));
    const need = Number(reward.points_required);
    const remaining = Math.max(0, need - have);
    return { reward, have, need, remaining, ready: remaining === 0 };
  });
  const ready = goals.filter((g) => g.ready).sort((a, b) => a.need - b.need);
  if (ready.length) return ready[0];
  return goals.sort((a, b) => a.remaining - b.remaining || a.need - b.need)[0];
}

/** "1 star" / "5 stars" etc. — picks the singular or plural translation key. */
export function pluralKey(base: string, n: number): string {
  return `${base}_${n === 1 ? 'one' : 'other'}`;
}
